import SYSTEM_MODULES from "constants/modules"

/** Legacy FNB/B2C brand toggles — superseded by plan.brandIds; hide from plan builder. */
export const LEGACY_PLAN_BRAND_PERMISSION_CATEGORY_ID = 44

/** Canonical permission category id for Zones (matches backend dbMigration). */
export const ZONE_PERMISSION_CATEGORY_ID = 43

/** Countries permission category — tenants may only view; platform manages CRUD. */
export const COUNTRY_PERMISSION_CATEGORY_ID = 11

export const TENANT_COUNTRY_MUTATION_PERMISSION_IDS = [
  "country_2",
  "country_3",
  "country_4",
]

/** Legacy plan mapping used 42 before Zones were assigned category 43. */
const LEGACY_ZONE_PERMISSION_CATEGORY_ID = 42

export const PLAN_FEATURE_TO_PERMISSION_CATEGORIES = {
  dashboard: [1],
  banners: [2],
  offer_banners: [49],
  roles: [3, 6],
  user_activity: [36],
  menus: [4, 23, 24],
  locations: [5, 11, 25, 26, ZONE_PERMISSION_CATEGORY_ID],
  customers: [7],
  orders: [9],
  promotions: [14, 46],
  loyalty: [20],
  gift_cards: [37, 38, 40],
  bookings: [21],
  notifications: [15],
  campaign: [],
  customer_support: [17],
  reports: [16, 27, 45],
  api_logs: [13],
  pos_settings: [28, 29, 30, 58],
  qr_codes: [22],
  dynamic_link: [39],
  vehicles: [35],
  brand_management: [52],
  delivery_partners: [47, 54, 55, 56],
  billing: [51],
  // Category 48 is driven by plan.integrationIds (see planIncludesIntegrationsModule).
  settings: [12, 18, 19, 20, 23, 24, 31, 32, 33, 34, 41, 50, 53, 59, 60],
}

const FEATURE_KEY_ALIASES = {
  locations_branches: "locations",
  branches: "locations",
  branch: "locations",
  roles_users: "roles",
  users: "roles",
  user_activity: "user_activity",
  users_activity: "user_activity",
  customer_management: "customers",
  promotions_coupons: "promotions",
  loyalty_programme: "loyalty",
  loyalty_program: "loyalty",
  reports_analytics: "reports",
  app_settings: "settings",
  dynamic_links: "dynamic_link",
  delivery_partner: "delivery_partners",
  "delivery-partners": "delivery_partners",
  delivery_partners: "delivery_partners",
  brands: "brand_management",
  brand_management: "brand_management",
  "brand-management": "brand_management",
  api_logs: "api_logs",
  qr_codes: "qr_codes",
  web_item_preview: "menus",
  mobile_item_preview: "menus",
  "web-item-preview": "menus",
  "mobile-item-preview": "menus",
  integration: "integrations",
  third_party_integrations: "integrations",
  "third-party-integrations": "integrations",
  pos_integration: "integrations",
}

export const normalizePlanFeatureKey = value => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")

  if (!normalized) return ""
  return FEATURE_KEY_ALIASES[normalized] || normalized
}

const normalizeNumericSet = values =>
  new Set(
    (values || [])
      .map(value => Number(value))
      .filter(value => !Number.isNaN(value))
  )

export const TENANT_INTEGRATIONS_PERMISSION_CATEGORY_ID = 48

export const SOCIAL_AUTH_SETTINGS_PERMISSION_CATEGORY_ID = 53

const extractFeatureKeys = plan =>
  (plan?.features || [])
    .map(feature =>
      normalizePlanFeatureKey(
        feature?.name || feature?.id || feature?.moduleId || feature?.code || ""
      )
    )
    .filter(Boolean)

/** Plan includes integrations catalogue rows (plan.integrationIds only). */
export const planIncludesIntegrationsModule = plan => {
  if (!plan || typeof plan !== "object") return false

  if (Array.isArray(plan.integrationIds) && plan.integrationIds.length > 0) {
    return true
  }

  return (plan.features || []).some(
    feature =>
      normalizePlanFeatureKey(
        feature?.name || feature?.id || feature?.moduleId || feature?.code || ""
      ) === "integrations" &&
      Array.isArray(feature.integrationIds) &&
      feature.integrationIds.length > 0
  )
}

const addCategoryIdsFromFeatureKeys = (derived, featureKeys) => {
  featureKeys.forEach(moduleKey => {
    ;(PLAN_FEATURE_TO_PERMISSION_CATEGORIES[moduleKey] || []).forEach(categoryId =>
      derived.add(categoryId)
    )
  })
}

const reconcileIntegrationsCategory = (categorySet, plan) => {
  if (!(categorySet instanceof Set)) return categorySet
  if (planIncludesIntegrationsModule(plan)) {
    categorySet.add(TENANT_INTEGRATIONS_PERMISSION_CATEGORY_ID)
  } else {
    categorySet.delete(TENANT_INTEGRATIONS_PERMISSION_CATEGORY_ID)
  }
  return categorySet
}

const reconcileLegacyZoneCategory = categorySet => {
  if (!(categorySet instanceof Set)) return categorySet
  if (categorySet.has(LEGACY_ZONE_PERMISSION_CATEGORY_ID)) {
    categorySet.add(ZONE_PERMISSION_CATEGORY_ID)
  }
  return categorySet
}

/**
 * Resolves which permission category ids the tenant plan allows.
 * Matches portal-backend resolveAllowedPermissionCategoryIds (permissionGrants win when present).
 */
export const getAllowedPermissionCategoryIdsFromPlan = subscription => {
  const plan = subscription?.planId || subscription?.plan || null
  if (!plan || typeof plan !== "object") return null

  if (Array.isArray(plan.permissionGrants) && plan.permissionGrants.length > 0) {
    const fromGrants = new Set()
    plan.permissionGrants.forEach(grant => {
      const categoryId = Number(grant?.categoryId)
      if (!Number.isNaN(categoryId)) fromGrants.add(categoryId)
    })
    if (fromGrants.size === 0) return null
    return reconcileLegacyZoneCategory(reconcileIntegrationsCategory(fromGrants, plan))
  }

  if (Array.isArray(plan.basePermissions) && plan.basePermissions.length > 0) {
    const fromBase = normalizeNumericSet(plan.basePermissions)
    if (fromBase.size === 0) return null
    return reconcileLegacyZoneCategory(reconcileIntegrationsCategory(fromBase, plan))
  }

  const featureKeys = extractFeatureKeys(plan)
  if (featureKeys.length === 0) return null

  const derived = new Set()
  addCategoryIdsFromFeatureKeys(derived, featureKeys)
  return reconcileLegacyZoneCategory(reconcileIntegrationsCategory(derived, plan))
}

export const PLAN_INTEGRATIONS_FEATURE_KEY = "integrations"

export const SYSTEM_MODULE_ID_SET = new Set(SYSTEM_MODULES.map(module => module.id))

export const isPlanIntegrationsFeature = name =>
  normalizePlanFeatureKey(name) === PLAN_INTEGRATIONS_FEATURE_KEY

/** Counts selectable plan-builder modules (excludes integrations feature rows and unknown keys). */
export const parsePlanModuleIds = (plan = {}) => {
  const ids = []
  const seen = new Set()

  const consume = rawName => {
    const moduleId = normalizePlanFeatureKey(rawName)
    if (!moduleId || moduleId === PLAN_INTEGRATIONS_FEATURE_KEY) return
    if (!SYSTEM_MODULE_ID_SET.has(moduleId)) return
    if (seen.has(moduleId)) return
    seen.add(moduleId)
    ids.push(moduleId)
  }

  if (Array.isArray(plan.features)) {
    plan.features.forEach(feature => consume(feature?.name))
  } else if (Array.isArray(plan.modules)) {
    plan.modules.forEach(module => {
      consume(typeof module === "string" ? module : module?.id || module?.name)
    })
  }

  return ids
}

export const countPlanModules = plan => parsePlanModuleIds(plan).length

export const planFeaturesToSelectedModules = (plan = {}) => {
  const limitByModuleId = new Map()

  if (Array.isArray(plan.features)) {
    plan.features.forEach(feature => {
      const moduleId = normalizePlanFeatureKey(feature?.name)
      if (!moduleId || moduleId === PLAN_INTEGRATIONS_FEATURE_KEY) return
      if (!SYSTEM_MODULE_ID_SET.has(moduleId)) return
      limitByModuleId.set(moduleId, feature?.limit)
    })
  }

  if (limitByModuleId.size > 0) {
    return parsePlanModuleIds(plan).map(id => ({
      id,
      isDefault: limitByModuleId.get(id) === "default",
    }))
  }

  if (!Array.isArray(plan.modules)) return []

  const seen = new Set()
  return plan.modules
    .map(module =>
      typeof module === "string"
        ? { id: normalizePlanFeatureKey(module), isDefault: false }
        : {
            id: normalizePlanFeatureKey(module?.id || module?.name),
            isDefault: !!module?.isDefault,
          }
    )
    .filter(entry => {
      if (!entry.id || !SYSTEM_MODULE_ID_SET.has(entry.id)) return false
      if (seen.has(entry.id)) return false
      seen.add(entry.id)
      return true
    })
}

export const sanitizeSelectedPlanModules = selectedModules => {
  const seen = new Set()
  return (selectedModules || [])
    .map(entry => ({
      ...entry,
      id: normalizePlanFeatureKey(entry?.id),
    }))
    .filter(entry => {
      if (!entry.id || !SYSTEM_MODULE_ID_SET.has(entry.id)) return false
      if (seen.has(entry.id)) return false
      seen.add(entry.id)
      return true
    })
}
